import { useState } from "react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Send, Undo2 } from "lucide-react";
import { TestoAdmin } from "@/pages/trattative/TrattativaConsensi";
import { ComunicazioneCompagnia } from "@/pages/trattative/QuestionarioBox";
import { DateInputIT } from "@/components/DateInputIT";

const d = (v) => (v ? new Date(v).toLocaleDateString("it-IT") : "—");
const dt = (v) => (v ? new Date(v).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const COLORI = { aperto: "text-amber-700", esercitato: "text-red-700", scaduto: "text-slate-600", non_registrato: "text-slate-500" };
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function ContrattoForm({ t, onDone }) {
  const c = t.contratto || {};
  const [f, setF] = useState({ data_conclusione: c.data_conclusione || "", data_consegna_documenti: c.data_consegna_documenti || "" });
  const salva = async () => { try { const { data } = await api.put(`/trattative/${t.id}/contratto`, f); onDone(data); toast.success("Contratto registrato"); } catch (e) { err(e); } };
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
      <label className="text-xs text-slate-600 space-y-1"><span>Contratto concluso il</span><DateInputIT value={f.data_conclusione} onChange={(v) => setF({ ...f, data_conclusione: v })} className="h-8" testid="rec-data-conclusione" /></label>
      <label className="text-xs text-slate-600 space-y-1"><span>Documenti contrattuali consegnati il</span><DateInputIT value={f.data_consegna_documenti} onChange={(v) => setF({ ...f, data_consegna_documenti: v })} className="h-8" testid="rec-data-consegna" /></label>
      <Button size="sm" variant="outline" onClick={salva} disabled={!f.data_conclusione} data-testid="rec-contratto-salva">Salva</Button>
    </div>
  );
}

function PaginaCliente({ t, onReload }) {
  const [opts, setOpts] = useState({ via_sms: true, via_email: !!t.cliente.email });
  const [busy, setBusy] = useState(false);
  const pc = t.pagina_cliente;
  const invia = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/trattative/${t.id}/pagina-cliente/invia`, { origin: window.location.origin, ...opts }); toast.success(`Pagina inviata via ${data.inviato_via.join(" e ")}`); onReload(); }
    catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      <p className="text-sm text-slate-700" data-testid="rec-pagina-stato">{pc?.inviata_at ? <>Pagina personale inviata il {dt(pc.inviata_at)}{pc.visualizzata_at ? `, aperta dal cliente il ${dt(pc.visualizzata_at)}` : ", non ancora aperta"}.</> : "Pagina personale non ancora inviata."}
        {t.info_recesso?.consegnata_at && <span className="text-emerald-700"> Informativa sul recesso v. {t.info_recesso.versione} consegnata.</span>}</p>
      <div className="flex items-center gap-4 text-sm flex-wrap">
        <label className="flex items-center gap-1.5"><Checkbox checked={opts.via_sms} onCheckedChange={(v) => setOpts({ ...opts, via_sms: !!v })} data-testid="rec-pagina-sms" /> SMS</label>
        <label className={`flex items-center gap-1.5 ${t.cliente.email ? "" : "opacity-50"}`}><Checkbox checked={opts.via_email} disabled={!t.cliente.email} onCheckedChange={(v) => setOpts({ ...opts, via_email: !!v })} data-testid="rec-pagina-email" /> Email</label>
        <Button size="sm" variant="outline" onClick={invia} disabled={busy || (!opts.via_sms && !opts.via_email)} data-testid="rec-pagina-invia"><Send className="h-3.5 w-3.5 mr-1" /> {pc?.inviata_at ? "Reinvia" : "Invia pagina personale"}</Button>
      </div>
    </div>
  );
}

export function RecessoBox({ t, onDone, onReload }) {
  const { user } = useAuth();
  const st = t.recesso_stato || {};
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="trt-recesso-box">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Undo2 className="h-4 w-4 text-teal-700" /> Contratto e diritto di recesso</h3>
        {user?.role === "admin" && <TestoAdmin path="/trattative/informativa-recesso/config" titolo="Informativa sul diritto di recesso" tid="rec-info" />}
      </div>
      <div className={`text-sm font-medium ${COLORI[st.stato] || ""}`} data-testid="rec-stato">
        {st.label}{st.scadenza && st.stato !== "esercitato" && ` (scadenza ${d(st.scadenza)})`}
        {t.contratto && <span className="block text-xs text-slate-500 font-normal">Termine di {t.contratto.giorni_recesso} giorni dal {d(t.contratto.decorrenza)} (data più recente tra conclusione e consegna dei documenti).</span>}
      </div>
      {t.recesso?.confermato_at ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800" data-testid="rec-esercitato">Il cliente ha esercitato il recesso online il {dt(t.recesso.confermato_at)} (confermato con OTP){t.recesso.motivo && ` — motivo: ${t.recesso.motivo}`}.{" "}
          <a href={`${API}/trattative/${t.id}/recesso/ricevuta`} target="_blank" rel="noreferrer" className="underline" data-testid="rec-ricevuta"><FileText className="inline h-3.5 w-3.5" /> ricevuta</a>
          <span className="block text-xs mt-1">Trasmetta la comunicazione alla Compagnia e gestisca il rimborso del premio non goduto.</span></div>
      ) : <ContrattoForm key={t.contratto?.at || "n"} t={t} onDone={onDone} />}
      {t.recesso?.confermato_at && <ComunicazioneCompagnia t={t} onDone={onDone} />}
      <div className="pt-3 border-t"><PaginaCliente t={t} onReload={onReload} /></div>
    </div>
  );
}
